import React, { useEffect, useRef, useState } from "react";
import { subscriptionApi } from "@shared/api/subscription.api";
import { useAuth } from "@shared/contexts/auth-context";
import { extractErrorDetail } from "@shared/helper_lib/api-error";
import { useToast } from "@shared/helper_lib/hooks/use-toast";
import { sendMetrikaGoal } from "@shared/helper_lib/metrika";
import { cn } from "@shared/helper_lib/utils";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@shared/ui/components";

import { EmailRequestModal } from "./EmailRequestModal";
import { getPaymentMethodDisplay } from "./payment-method-label";
import { useLinkedCard, useSeamlessCharge } from "./useSeamlessCharge";

interface BuyMinutesModalProps {
  open: boolean;
  onClose: () => void;
  balance: {
    planName: string;
    extraMinutePrice: number;
  };
  onSuccess?: () => void;
  planName?: string;
}

const PRESET_PACKS = [
  { minutes: 100, popular: true },
  { minutes: 500, popular: false },
  { minutes: 1000, popular: false }
];

export const BuyMinutesModal: React.FC<BuyMinutesModalProps> = ({ open, onClose, balance, onSuccess, planName }) => {
  const { needsEmailForPayment, updateUserEmail } = useAuth();
  const { toast } = useToast();
  const isTrial = planName === "trial";
  const { card, hasCard, isLoading: isCardLoading } = useLinkedCard(open && !isTrial);
  const { charge, isCharging, failure, reset: resetFailure } = useSeamlessCharge();
  const isFreePlan = planName === "free";
  const availablePacks = PRESET_PACKS;
  const defaultPack = isFreePlan ? 100 : 500;

  const [selectedPack, setSelectedPack] = useState<number>(defaultPack);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const purchaseInFlightRef = useRef(false);

  useEffect(() => {
    setSelectedPack(defaultPack);
  }, [defaultPack]);

  const totalCost = selectedPack * balance.extraMinutePrice;

  const handlePurchase = async (): Promise<void> => {
    if (needsEmailForPayment) {
      setShowEmailModal(true);
      return;
    }

    await proceedWithPayment();
  };

  const handleEmailSuccess = async (email: string): Promise<void> => {
    updateUserEmail(email);
    setShowEmailModal(false);
    await proceedWithPayment();
  };

  const proceedWithPayment = async (): Promise<void> => {
    const validPacks = availablePacks.map(pack => pack.minutes);
    if (!validPacks.includes(selectedPack)) {
      setError(`Доступны только пакеты ${validPacks.join(", ")} минут`);
      return;
    }

    // Кнопка блокируется только после рендера: второй клик в том же такте
    // успел бы отправить второе списание
    if (purchaseInFlightRef.current) {
      return;
    }
    purchaseInFlightRef.current = true;

    setIsLoading(true);
    setError(null);

    try {
      sendMetrikaGoal("checkout_opened", { checkout_plan: "pack", pack_minutes: selectedPack });

      // Карта уже сохранена — списываем на месте, человек остаётся на своей
      // странице. Редирект на ЮKassa остаётся фоллбэком при отказе банка.
      if (hasCard) {
        const result = await charge({ kind: "minutes", minutes: selectedPack });

        if (result?.status === "succeeded") {
          toast({
            title: "Минуты зачислены",
            description: `+${selectedPack} минут на ваш баланс`
          });
          onSuccess?.();
          handleClose();
        }

        return;
      }

      await redirectToCheckout();
    } catch (err: any) {
      console.error("[MINUTES_PAYMENT] Error:", err);
      setError(extractErrorDetail(err, "Не удалось создать платёж. Попробуйте позже."));
    } finally {
      purchaseInFlightRef.current = false;
      setIsLoading(false);
    }
  };

  /** Старый путь: ссылка ЮKassa в новой вкладке */
  const redirectToCheckout = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await subscriptionApi.createMinutesPayment(selectedPack);

      if (!result.paymentUrl) {
        console.error("[MINUTES_PAYMENT] No paymentUrl in response:", result);
        setError("Ошибка: не получена ссылка для оплаты");
        return;
      }

      window.open(result.paymentUrl, "_blank");
    } catch (err: any) {
      console.error("[MINUTES_PAYMENT] Error:", err);
      setError(extractErrorDetail(err, "Не удалось создать платёж. Попробуйте позже."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (): void => {
    setSelectedPack(defaultPack);
    setError(null);
    resetFailure();
    onClose();
  };

  if (isTrial) {
    return (
      <Dialog open={open} onOpenChange={open => !open && handleClose()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Докупить минуты</DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <p className="text-lg mb-2">Докупка минут недоступна</p>
            <p className="text-muted-foreground">На пробном периоде нельзя докупать минуты.</p>
          </div>
          <DialogFooter>
            <Button onClick={handleClose}>Понятно</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Докупить минуты</DialogTitle>
          <DialogDescription>
            Цена для тарифа &quot;{balance.planName}&quot;: {balance.extraMinutePrice}₽/мин
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Предустановленные пакеты */}
          <div className="grid grid-cols-3 gap-3 pt-2 sm:gap-4">
            {availablePacks.map(pack => (
              <Card
                key={pack.minutes}
                className={cn(
                  "cursor-pointer hover:border-primary transition-colors relative py-3 sm:py-6",
                  selectedPack === pack.minutes && "border-primary border-2"
                )}
                onClick={() => {
                  setSelectedPack(pack.minutes);
                  setError(null);
                  resetFailure();
                }}
              >
                {pack.popular && (
                  <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 px-1.5 text-[10px] sm:text-xs">
                    Популярный<span className="hidden sm:inline">&nbsp;выбор</span>
                  </Badge>
                )}
                <CardContent className="px-2 py-1 text-center sm:p-4">
                  <p className="text-2xl font-bold sm:text-3xl">{pack.minutes}</p>
                  <p className="text-sm text-muted-foreground mb-2">минут</p>
                  <p className="text-lg font-semibold sm:text-xl">
                    {(pack.minutes * balance.extraMinutePrice).toFixed(0)}₽
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {selectedPack > 0 && (
            <div className="text-center p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">Итого к оплате</p>
              <p className="text-2xl font-bold">{totalCost.toFixed(0)}₽</p>
            </div>
          )}

          {hasCard && card && !failure && (
            <div className="text-sm text-muted-foreground text-center">
              Спишем {totalCost.toFixed(0)}₽ с «{getPaymentMethodDisplay(card)}». Минуты зачислятся сразу.
            </div>
          )}

          {failure && (
            <div className="text-sm bg-muted p-3 rounded-md space-y-2">
              <p className={failure.canRetry ? "text-destructive" : undefined}>{failure.message}</p>
              {failure.canRetry && (
                <Button variant="outline" size="sm" onClick={redirectToCheckout} disabled={isLoading}>
                  Оплатить другой картой
                </Button>
              )}
            </div>
          )}

          {error && <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading || isCharging}>
            Отмена
          </Button>
          <Button onClick={handlePurchase} disabled={isLoading || isCharging || isCardLoading || selectedPack <= 0}>
            {isLoading || isCharging || isCardLoading
              ? "Обработка..."
              : hasCard
                ? `Оплатить ${totalCost.toFixed(0)}₽`
                : "Перейти к оплате"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <EmailRequestModal
        open={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccess={handleEmailSuccess}
      />
    </Dialog>
  );
};
